import type { ReactElement } from 'react';

import { BringgFontIcons } from '@bringg/bringg-icons';
import { LatLng, datetime, Location as BringgLocation } from '@bringg/types';

export type Anchor = 'center' | 'left' | 'right' | 'top' | 'bottom';
export const defaultAnchor: Anchor = 'bottom';
export type IdType = string | number;
export type MapboxIconOptions = { pixelRatio?: number; sdf?: boolean };

export type Size = {
	width: number;
	height: number;
};

export interface MarkerData {
	id: IdType;
	groupId?: IdType;
	location: BringgLocation;
	color?: string;
	textColor?: string;
	text?: string;
	anchor?: Anchor;
	icon?: BringgFontIcons | ReactElement | string;
	iconOptions?: MapboxIconOptions;
	size?: Size;
	data?: {
		[key: string]: any;
	};
}

export type RouteLocation = {
	lat: number;
	lng: number;
	happened_at: datetime;
};

type BringgMapEntityExtention = {
	shouldIncludeInBounds?: boolean;
	shouldAutoFocus?: boolean;
};

export enum TriggerType {
	CLICK = 'click',
	MOUSEOVER = 'mouseover'
}

type TooltipOptions = {
	trigger?: TriggerType;
};

export type TooltipElement = HTMLElement & {
	renderTooltip?: () => void;
	dispose?: () => void;
	onTooltipOpen?: () => void;
};

export type ExtendedMarkerData = BringgMapEntityExtention &
	MarkerData & {
		striped?: boolean;
		zIndex?: number;
		tooltip?: Element | string;
		tooltipOptions?: TooltipOptions;
		opacity?: number;
		title?: string;
		shouldCluster?: boolean;
		animation?: google.maps.Animation;
		markerTriggerListener?: google.maps.MapsEventListener;
		draggable?: boolean;
		onDragEnd?: (event: google.maps.MapMouseEvent) => void;
		extraIcon?: { icon: BringgFontIcons; className: string };
	};
export type ExtendedPolylineData = BringgMapEntityExtention & {
	id: IdType;
	path: RouteLocation[];
	color?: string;
	strokeOpacity?: number;
	strokeWeight?: number;
	onMouseover?: (polyline: google.maps.Polyline) => void;
	onMouseout?: (polyline: google.maps.Polyline) => void;
};
export type BringgMapTranslations = {
	noRouteLocations?: string;
	liveTracking?: string;
	traffic?: string;
};
export type MapOptionsProps = {
	markers?: ExtendedMarkerData[];
	polylines?: ExtendedPolylineData[];
	mapCenter?: LatLng;
	mapZoom?: number;
	maxZoom?: number;
	minZoom?: number;
	isPlaying?: boolean;
	forceRecenterOnAddingMarkers?: boolean; // coordinate change wont trigger recenter
	forceRecenterOnUpdatingMarkers?: boolean;
	mapElementClassName?: string;
	mapContainerClassName?: string;
	fullscreenControl?: boolean;
	allowTrafficLayer?: boolean;
	translations?: BringgMapTranslations;
	buttons?: ButtonPropType[];
	['data-test-id']?: string;
	selectedMarkers?: IdType[];
	onSelectMarker?: (isCtrlClick: boolean, markerId: IdType) => void;
	clearSelectedMarkers?: () => void;
	onMapClick?: (e: google.maps.MapMouseEvent) => void;
	boundsPadding?: number;
	options?: google.maps.MapOptions;
	clusterOptions?: ClusterOptions;
	localStorageKey?: string;
};

export type ClusterOptions = {
	cluster: boolean;
	/** Route-colored grouped cluster UI at higher zoom (monitoring: `dpv2_cluster_color_by_route`). */
	clusterColorByRoute?: boolean;
	groupedClusterMinZoom?: number;
	getClusterTooltip?: (markersData: ExtendedMarkerData[]) => HTMLDivElement | string;
	onClusterClick?: (isCtrlClick: boolean, markers: ExtendedMarkerData[]) => void;
};

export type BringgMapPropsV2 = Omit<MapOptionsProps, 'isPlaying'> & {
	disablePlayer?: boolean;
	showPlayer?: boolean;
	timeZone?: string;
	timeFormat?: string;
	onMarkerClick?: (markerId: IdType, isCtrlClick: boolean) => void;
	onMapClick?: (e: google.maps.MapMouseEvent) => void;
	boundsPadding?: number;
	options?: google.maps.MapOptions;
	clusterOptions?: ClusterOptions;
	localStorageKey?: string;
};

export type ButtonPropType = {
	text: string;
	['data-test-id']: string;
	className?: string;
	clickHandler: (e: MouseEvent) => void;
};
